import type { ReactNode, RefObject } from "react";
import { Menu, X } from "lucide-react";
import type { NavGroup } from "../features/registry";
import { navigateOnPlainClick } from "./NavTabs";

interface NavDrawerToggleProps {
  isOpen: boolean;
  onOpen: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
}

/**
 * The hamburger button that opens the drawer. Rendered in normal document
 * flow inside the header (stacked above the title, both flush with the
 * body's left edge) rather than floated over it — see NavDrawerPanel for
 * why the close affordance lives separately, in the panel itself.
 */
export function NavDrawerToggle({ isOpen, onOpen, buttonRef }: NavDrawerToggleProps) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className="nav-drawer__toggle"
      aria-expanded={isOpen}
      aria-controls="nav-drawer-panel"
      aria-label="Open menu"
      tabIndex={isOpen ? -1 : undefined}
      onClick={onOpen}
    >
      <Menu aria-hidden="true" size={22} />
    </button>
  );
}

interface NavDrawerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  groups: NavGroup[];
  /** The path of the page being shown, which the matching entry marks as current. */
  activePath: string | null;
  onNavigate: (path: string) => void;
  /** Rendered under an "Account" heading — login/logout/edit-toggle content
   *  built by App.tsx, same composition pattern as the old header authSlot. */
  accountSlot: ReactNode;
  /** Attached to the panel <nav> (which is tabIndex=-1). useNavDrawer
   *  focuses it on open and uses it to scope the Tab-trap's focusable
   *  query without walking the whole document. */
  panelRef: RefObject<HTMLElement>;
}

/**
 * The drawer's backdrop + sliding panel. Since the panel overlays the
 * header (and thus NavDrawerToggle) whenever it's open, it carries its own
 * close button rather than relying on the toggle underneath it.
 *
 * Always mounted (visibility toggled via CSS transform + aria-hidden, not
 * conditional rendering) so both the open and close transitions animate.
 */
export function NavDrawerPanel({
  isOpen,
  onClose,
  groups,
  activePath,
  onNavigate,
  accountSlot,
  panelRef,
}: NavDrawerPanelProps) {
  function go(path: string) {
    onNavigate(path);
    onClose();
  }

  return (
    <>
      <div
        className="nav-drawer__backdrop"
        aria-hidden="true"
        data-open={isOpen}
        onClick={onClose}
      />

      <nav
        ref={panelRef}
        id="nav-drawer-panel"
        className="nav-drawer__panel"
        aria-label="Site navigation"
        aria-hidden={!isOpen}
        data-open={isOpen}
        tabIndex={-1}
      >
        <button type="button" className="nav-drawer__close" aria-label="Close menu" onClick={onClose}>
          <X aria-hidden="true" size={20} />
        </button>

        <ul className="nav-drawer__list">
          {groups.map((group) => {
            const Icon = group.icon;
            const link = (page: NavGroup["pages"][number], nested: boolean) => (
              <a
                href={page.path}
                className={nested ? "nav-drawer__item nav-drawer__item--nested" : "nav-drawer__item"}
                aria-current={page.path === activePath ? "page" : undefined}
                onClick={navigateOnPlainClick(() => go(page.path))}
              >
                {!nested && <Icon className="nav-drawer__item-icon" aria-hidden="true" size={18} />}
                <span>{page.label}</span>
              </a>
            );
            // A group of one is just its page; a bigger one is a heading with its pages beneath.
            if (group.pages.length === 1) return <li key={group.id}>{link(group.pages[0]!, false)}</li>;
            return (
              <li key={group.id} className="nav-drawer__group">
                <span className="nav-drawer__group-label">
                  <Icon className="nav-drawer__item-icon" aria-hidden="true" size={18} />
                  {group.label}
                </span>
                <ul className="nav-drawer__sublist">
                  {group.pages.map((page) => (
                    <li key={page.path}>{link(page, true)}</li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>

        <div className="nav-drawer__divider" />

        <h2 className="nav-drawer__heading">Account</h2>
        <div className="nav-drawer__account">{accountSlot}</div>
      </nav>
    </>
  );
}
